import { Link } from "react-router-dom";
import { Search, ClipboardList, MessageCircle, PawPrint, Home, FileText, Sparkles, HelpCircle } from "lucide-react";
import type { Article } from "./types";
import { articlesMeta } from "./_meta";
import { SectionHeading } from "@/components/article/SectionHeading";
import { Callout } from "@/components/article/Callout";

const META = articlesMeta.find((m) => m.slug === "jak-wyglada-adopcja-psa-lub-kota-ze-schroniska")!;

const Body = () => (
  <>
    <p>
      Dużo osób przed pierwszą adopcją stresuje się samym procesem. Ankiety, rozmowy z fundacją albo schroniskiem
      czy obawa, że „powie się coś nie tak", są całkowicie normalne. W praktyce większość organizacji chce po
      prostu dobrze dopasować zwierzaka do nowego domu i zmniejszyć ryzyko nieudanej adopcji.
    </p>
    <p>
      Sam proces może wyglądać trochę inaczej w zależności od organizacji, ale zwykle przebiega podobnie.
    </p>

    <SectionHeading id="wybor-zwierzaka" icon={Search}>
      1. Wybór zwierzaka
    </SectionHeading>
    <p>
      Najczęściej wszystko zaczyna się od przeglądania ogłoszeń albo wizyty w schronisku. Wiele organizacji
      publikuje opisy charakteru zwierząt, informacje o zdrowiu czy relacjach z innymi zwierzętami.
    </p>
    <p>
      Przy psach często można wcześniej umówić się na <strong>spacer zapoznawczy</strong>. Przy kotach zwykle da
      się spędzić trochę czasu w kociarni albo domu tymczasowym. Dzięki temu łatwiej ocenić, czy to faktycznie
      dobre dopasowanie.
    </p>

    <SectionHeading id="ankieta" icon={ClipboardList}>
      2. Ankieta przedadopcyjna
    </SectionHeading>
    <p>W większości schronisk i fundacji trzeba wypełnić ankietę przedadopcyjną.</p>
    <p>Pytania zwykle dotyczą:</p>
    <ul>
      <li>trybu życia,</li>
      <li>czasu spędzanego poza domem,</li>
      <li>doświadczenia ze zwierzętami,</li>
      <li>innych zwierząt w domu,</li>
      <li>zabezpieczenia balkonu i okien przy adopcji kota.</li>
    </ul>
    <p>Często pojawia się też pytanie, czy wszyscy domownicy zgadzają się na adopcję.</p>
    <p>
      To nie jest „test idealnego opiekuna". Chodzi bardziej o to, żeby zwierzę trafiło do domu, w którym będzie
      mu naprawdę dobrze. Niektóre organizacje proszą też o zdjęcia mieszkania albo balkonu.
    </p>

    <SectionHeading id="rozmowa" icon={MessageCircle}>
      3. Rozmowa z fundacją lub schroniskiem
    </SectionHeading>
    <p>Po przejrzeniu ankiety organizacja zwykle kontaktuje się telefonicznie albo mailowo.</p>
    <p>Często pojawiają się dodatkowe pytania dotyczące:</p>
    <ul>
      <li>trybu pracy i godzin spędzanych poza domem,</li>
      <li>tego, czy zwierzę będzie zostawało samo,</li>
      <li>oczekiwań wobec adopcji,</li>
      <li>tego, czy w domu są dzieci albo inne zwierzęta.</li>
    </ul>
    <p>Przy kotach bardzo często pojawia się też temat zabezpieczenia okien i balkonu.</p>

    <SectionHeading id="spotkanie" icon={PawPrint}>
      4. Spotkanie ze zwierzakiem
    </SectionHeading>
    <p>To zwykle jeden z najważniejszych etapów całego procesu.</p>
    <p>
      Wiele organizacji chce zobaczyć, jak zwierzę reaguje na potencjalnego opiekuna i czy charakter faktycznie
      pasuje do stylu życia. Przy psach często odbywają się spacery zapoznawcze, czasem nawet kilka.
    </p>

    <SectionHeading id="wizyta" icon={Home}>
      5. Wizyta przedadopcyjna
    </SectionHeading>
    <p>Nie każda organizacja ją robi, ale jest to dość częsta praktyka.</p>
    <p>Najczęściej chodzi po prostu o sprawdzenie:</p>
    <ul>
      <li>czy mieszkanie jest bezpieczne,</li>
      <li>czy balkon jest zabezpieczony,</li>
      <li>czy warunki są zgodne z informacjami z ankiety.</li>
    </ul>
    <p>W praktyce zwykle nie wygląda to jak „kontrola", tylko raczej normalna rozmowa.</p>

    <SectionHeading id="umowa" icon={FileText}>
      6. Umowa adopcyjna
    </SectionHeading>
    <p>Przed adopcją podpisuje się umowę adopcyjną.</p>
    <p>Najczęściej znajdują się w niej informacje dotyczące:</p>
    <ul>
      <li>zapewnienia odpowiednich warunków,</li>
      <li>opieki weterynaryjnej,</li>
      <li>zakazu oddawania zwierzęcia dalej bez kontaktu z organizacją,</li>
      <li>obowiązku kastracji albo sterylizacji.</li>
    </ul>
    <p>
      Do adopcji zwykle potrzebny jest dokument tożsamości, a przy odbiorze kota bezpieczny transporter.
    </p>
    <Callout variant="info" title="Opłata adopcyjna">
      <p>
        Większość organizacji prosi o symboliczną opłatę adopcyjną. Nie jest to „cena zwierzęcia", tylko
        częściowy zwrot kosztów weterynaryjnych - i pieniądze, które bardzo często trafiają od razu na
        leczenie albo sterylizację kolejnego zwierzaka czekającego na dom.
      </p>
    </Callout>

    <SectionHeading id="pierwsze-dni" icon={Sparkles}>
      7. Pierwsze dni po adopcji
    </SectionHeading>
    <p>
      Nawet jeśli wszystko przebiegło bez problemu, zwierzę potrzebuje czasu na adaptację. Niektóre psy albo
      koty szybko odnajdują się w nowym miejscu, a inne przez pierwsze dni są wycofane i zestresowane.
    </p>
    <p>To całkowicie normalne.</p>
    <p>
      Większość organizacji po adopcji nadal pomaga i odpowiada na pytania nowych opiekunów, szczególnie jeśli
      pojawiają się wątpliwości związane z zachowaniem zwierzaka albo adaptacją.
    </p>

    <Callout variant="tip" title="A jeśli jeszcze nie jesteś gotowy/gotowa na adopcję?">
      <p>
        Dobrą alternatywą jest <strong>dom tymczasowy</strong>. To czasowa opieka nad zwierzakiem, zanim trafi
        do swojego docelowego domu - zwykle od kilku tygodni do kilku miesięcy. Karmę i opiekę weterynaryjną
        najczęściej pokrywa fundacja, a Ty dajesz zwierzakowi spokojne miejsce z dala od schroniska. To też
        świetny sposób, żeby sprawdzić, jak życie ze zwierzakiem wygląda na co dzień.
      </p>
    </Callout>

    <SectionHeading id="odmowa" icon={HelpCircle}>
      Czy organizacja może odmówić adopcji?
    </SectionHeading>
    <p>
      Tak. Organizacje mają prawo odmówić adopcji, jeśli uznają, że dane warunki nie będą odpowiednie dla
      konkretnego zwierzęcia.
    </p>
    <p>Nie chodzi jednak o „szukanie idealnych ludzi", tylko o bezpieczeństwo i dobro zwierzęcia.</p>
    <p>Czasem po prostu:</p>
    <ul>
      <li>charakter zwierzęcia nie pasuje do trybu życia,</li>
      <li>mieszkanie nie jest odpowiednio zabezpieczone,</li>
      <li>albo organizacja uważa, że inne zwierzę będzie lepiej dopasowane.</li>
    </ul>

    <SectionHeading id="ile-trwa" icon={HelpCircle}>
      Ile trwa adopcja?
    </SectionHeading>
    <p>
      To zależy od organizacji i konkretnego zwierzęcia. Czasem trwa kilka dni, a czasem kilka tygodni. Przy
      zwierzętach wymagających większego doświadczenia albo specjalnych warunków proces zwykle trwa dłużej.
    </p>

    <div className="not-prose my-8 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 p-5 sm:p-6 text-center">
      <p className="text-base sm:text-lg font-semibold text-foreground mb-3">
        Szukasz schroniska albo fundacji w swojej okolicy?
      </p>
      <p className="text-sm text-muted-foreground mb-4">
        Sprawdź organizacje i aktualne profile zwierząt na mapie.
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-5 py-2.5 rounded-full text-sm hover:bg-primary/90 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
      >
        Otwórz mapę schronisk
      </Link>
    </div>
  </>
);

const article: Article = {
  slug: META.slug,
  title: META.title,
  description: META.description,
  publishedAt: META.publishedAt,
  readingMinutes: 6,
  category: META.category,
  excerpt: META.excerpt,
  Body,
  icon: PawPrint,
  toc: [
    { id: "wybor-zwierzaka", label: "Wybór zwierzaka" },
    { id: "ankieta", label: "Ankieta przedadopcyjna" },
    { id: "rozmowa", label: "Rozmowa z fundacją lub schroniskiem" },
    { id: "spotkanie", label: "Spotkanie ze zwierzakiem" },
    { id: "wizyta", label: "Wizyta przedadopcyjna" },
    { id: "umowa", label: "Umowa adopcyjna" },
    { id: "pierwsze-dni", label: "Pierwsze dni po adopcji" },
    { id: "odmowa", label: "Czy organizacja może odmówić adopcji?" },
    { id: "ile-trwa", label: "Ile trwa adopcja?" },
  ],
};

export default article;
